import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { Page, useAsync } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { allTenants, tenantLabel } from '../../identity/TenantPicker';
import { activeTarget, authClientFor, tenantPath } from '../../identity/session';
import type { IdentityProviderType, Tenant } from '../../identity/api';
import { SCOPES, type Environment } from '../../api';
import { api } from '../../session';
import { targetOf, useOnboardingConfig } from '../api/config';
import { addAlias, addScopes, clientWith, missingScopes } from '../api/auth';
import { allCustomers, authenticateUrl, slug } from '../api/directory';
import { DEFAULT_MODULES, MODULES, clientScopesFor, tenantScopesFor } from '../api/modules';
import { workServerFiles } from '../generate/workserver';
import { guidePath } from '../guides';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, NeedsScope, Note, SecretValue, StepActions, useAction, useScope } from '../wizard/parts';

/** W1: AUTH customer and tenant of an installation, its claims, keys, aliases and service client, and the
 *  WorkServer's options. Covers box calculation completely. */
export function TenantWizardPage() {
  return (
    <Page
      title="Customer & tenant"
      description="Create or complete a customer's AUTH tenant for one installation: claims for the modules it uses, service key, aliases, the WorkServer's service client and its options. Every step checks AUTH first."
    >
      <TargetShell>
        <TenantWizard />
      </TargetShell>
    </Page>
  );
}

function TenantWizard() {
  const target = activeTarget();
  const client = authClientFor(target);
  const config = useOnboardingConfig();
  const env = targetOf(config, target);
  const isHubAuth = !!config && config.hubTarget === target;
  const canAdmin = useScope(SCOPES.admin);
  const secrets = useSecrets();
  const [customerId, setCustomerId] = useWizardParam('customer');
  const [tenantId, setTenantId] = useWizardParam('tenant');
  const [modulesParam, setModules] = useWizardParam('modules');
  const [ws, setWs] = useWizardParam('ws');
  const [ns, setNs] = useWizardParam('ns');
  const [envId, setEnvId] = useWizardParam('env');
  const [secretIn, setSecretIn] = useWizardParam('secretIn');
  const modules = modulesParam ? modulesParam.split(',').filter(Boolean) : DEFAULT_MODULES;

  const [newCustomer, setNewCustomer] = useState('');
  const [pms, setPms] = useState('');
  const [form, setForm] = useState({
    name: '',
    provider: 'Workserver' as IdentityProviderType,
    url: '',
    template: '',
  });
  const [gke, setGke] = useState('');

  const dir = useAsync(async () => {
    const [customers, tenants] = await Promise.all([allCustomers(target), allTenants(target)]);
    return { customers, tenants };
  }, [target]);
  const state = useAsync(async () => {
    if (!tenantId) return null;
    const [tenant, aliases, clients, keys, envs] = await Promise.all([
      client.tenants.get(tenantId),
      client.tenants.aliases.list(tenantId),
      client.tenants.clients.list(tenantId),
      client.tenants.serviceKeys.list(tenantId),
      isHubAuth
        ? (canAdmin ? api.admin.environments.list() : api.environments()).catch(() => [] as Environment[])
        : Promise.resolve([] as Environment[]),
    ]);
    return { tenant, aliases, clients, keys, envs };
  }, [target, tenantId, isHubAuth, canAdmin]);
  const reload = async () => {
    await Promise.all([dir.reload(), state.reload()]);
  };
  const action = useAction(reload);
  const s = state.data;
  const customer = dir.data?.customers.find((c) => c.id === customerId);
  const tenant = s?.tenant;
  const wanted = tenantScopesFor(modules);
  const missing = missingScopes(tenant?.claims, wanted);
  const clientScopes = clientScopesFor(modules);
  const wms = clientWith(s?.clients, clientScopes);
  const wmsAny = s?.clients.find((c) => c.name.startsWith('wms'));
  const activeKey = s?.keys.find((k) => !k.revokedAt);
  const wsAlias = s?.aliases.find((a) => a.system === 'workserver');
  const linkedEnv = s?.envs.find((e) => e.authTenantId?.toLowerCase() === tenantId.toLowerCase());
  const sk = (k: string) => `tenant:${target}:${tenantId}:${k}`;
  const clientSecret = secrets(sk('client-secret'));
  const serviceKey = secrets(sk('service-key'));
  const customerTenants = (dir.data?.tenants ?? []).filter((t) => t.customerId === customerId);
  const toggleModule = (id: string, on: boolean) =>
    setModules((on ? [...modules, id] : modules.filter((m) => m !== id)).join(',') || 'none');

  const steps: WizardStep[] = [
    {
      id: 'customer',
      title: 'Customer',
      state: customer ? 'done' : 'todo',
      summary: customer?.name,
      body: (
        <>
          <p>
            The customer is the organization; its name becomes the <code>orgid</code> of every token. Pick it
            or create it.
          </p>
          <Select
            label="Customer"
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setTenantId('');
            }}
            searchable
            options={[
              { value: '', label: '— pick or create —' },
              ...(dir.data?.customers ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <div className="nyk-form">
            <TextField
              label="New customer"
              value={newCustomer}
              onChange={(e) => setNewCustomer(e.target.value)}
            />
            <TextField
              label="PMS customer id"
              inputMode="numeric"
              value={pms}
              onChange={(e) => setPms(e.target.value.replace(/\D/g, ''))}
              hint="Optional: alias pms:<id>, links Statistics."
            />
          </div>
          <StepActions error={action.error}>
            <Button
              disabled={!newCustomer.trim()}
              loading={action.busy === 'customer'}
              onClick={() =>
                void action.run('customer', async () => {
                  const c = await client.customers.create({ name: newCustomer.trim() });
                  if (pms) await addAlias(target, 'customers', c.id, 'pms', pms);
                  setCustomerId(c.id);
                  setNewCustomer('');
                })
              }
            >
              Create customer
            </Button>
            {customer && pms && (
              <Button
                variant="ghost"
                loading={action.busy === 'pms'}
                onClick={() =>
                  void action.run('pms', () => addAlias(target, 'customers', customer.id, 'pms', pms))
                }
              >
                Add pms:{pms} to {customer.name}
              </Button>
            )}
          </StepActions>
          {customer?.aliases?.length ? (
            <Facts rows={[['Aliases', customer.aliases.map((a) => `${a.system}:${a.value}`).join(', ')]]} />
          ) : null}
        </>
      ),
    },
    {
      id: 'tenant',
      title: 'Tenant',
      state: !customer ? 'blocked' : tenant ? 'done' : 'todo',
      blocked: 'Pick the customer first.',
      summary: tenant ? `${tenant.name} · ${tenant.identityProvider}` : undefined,
      body: (
        <>
          <p>
            One tenant per installation (production and test are separate). A <b>Workserver</b> tenant signs
            users in at the WorkServer; a <b>Database</b> tenant keeps its own users (and can take SCIM).
          </p>
          {!!customerTenants.length && (
            <Select
              label={`Existing tenants of ${customer?.name ?? ''}`}
              value={tenantId}
              onChange={setTenantId}
              options={[
                { value: '', label: '— or create one below —' },
                ...customerTenants.map((t) => ({ value: t.id, label: tenantLabel(t) })),
              ]}
            />
          )}
          {!tenant && (
            <>
              <div className="nyk-form">
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <Select
                  label="Users sign in at"
                  value={form.provider}
                  onChange={(v) => setForm({ ...form, provider: v as IdentityProviderType })}
                  options={[
                    { value: 'Workserver', label: 'the WorkServer (Workserver)' },
                    { value: 'Database', label: 'AUTH itself (Database)' },
                  ]}
                />
                <TextField
                  label={form.provider === 'Workserver' ? 'WorkServer URL' : 'Identity provider URL'}
                  className="nyk-url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://acme.nycelogic.com"
                  hint={
                    form.provider === 'Workserver'
                      ? `Used: ${authenticateUrl(form.url) || '…/api/authenticate'}`
                      : 'Not used for sign-in; any URL of the customer.'
                  }
                />
                <Select
                  label="Copy claims and roles from"
                  value={form.template}
                  onChange={(v) => setForm({ ...form, template: v })}
                  searchable
                  options={[
                    { value: '', label: '— nothing —' },
                    ...(dir.data?.tenants ?? []).map((t: Tenant) => ({ value: t.id, label: tenantLabel(t) })),
                  ]}
                />
              </div>
              <StepActions error={action.error}>
                <Button
                  disabled={!form.name.trim() || !/^https?:\/\/\S+$/.test(form.url.trim())}
                  loading={action.busy === 'tenant'}
                  onClick={() =>
                    void action.run('tenant', async () => {
                      const t = await client.tenants.create({
                        name: form.name.trim(),
                        customerId,
                        identityProvider: form.provider,
                        identityProviderUrl:
                          form.provider === 'Workserver' ? authenticateUrl(form.url) : form.url.trim(),
                      });
                      setTenantId(t.id);
                      if (form.template) {
                        const src = await client.tenants.get(form.template);
                        if (src.claims.length)
                          await client.tenants.claims.replace(
                            t.id,
                            src.claims.map(({ type, value }) => ({ type, value }))
                          );
                        if (src.roles.length)
                          await client.tenants.roles.replace(
                            t.id,
                            src.roles.map(({ name, scopes }) => ({ name, scopes }))
                          );
                      }
                    })
                  }
                >
                  Create tenant
                </Button>
              </StepActions>
            </>
          )}
          {tenant && (
            <Facts
              rows={[
                [
                  'Tenant',
                  <Link key="t" className="nyk-link" to={tenantPath(tenant.id)}>
                    {tenant.name}
                  </Link>,
                ],
                ['Id', <code key="i">{tenant.id}</code>],
                ['Sign-in', `${tenant.identityProvider} · ${tenant.identityProviderUrl}`],
              ]}
            />
          )}
          {tenant?.identityProvider === 'Workserver' &&
            !/\/api\/authenticate$/i.test(tenant.identityProviderUrl) && (
              <Note tone="warn">
                The URL should end in <code>/api/authenticate</code> — sign-in answers 502 otherwise. Fix it
                on the tenant page.
              </Note>
            )}
        </>
      ),
    },
    {
      id: 'modules',
      title: 'Modules and claims',
      state: !tenant ? 'blocked' : missing.length ? 'todo' : 'done',
      blocked: 'Pick or create the tenant first.',
      summary: tenant
        ? missing.length
          ? `${missing.length} scope(s) missing`
          : MODULES.filter((m) => modules.includes(m.id))
              .map((m) => m.label)
              .join(', ')
        : undefined,
      body: (
        <>
          <p>
            Which NYCE.LOGIC modules this installation uses: the tenant needs their scopes (a client never
            gets more).
          </p>
          <div className="ob-checks">
            {MODULES.map((m) => (
              <Switch
                key={m.id}
                checked={modules.includes(m.id)}
                onChange={(on) => toggleModule(m.id, on)}
                label={m.label}
              />
            ))}
          </div>
          <Facts
            rows={[
              ['Needed', wanted.join(', ') || '—'],
              ['Missing', missing.join(', ') || 'none'],
            ]}
          />
          <StepActions error={action.error}>
            <Button
              disabled={!missing.length}
              loading={action.busy === 'claims'}
              onClick={() => void action.run('claims', () => addScopes(target, tenantId, missing))}
            >
              Add missing scopes
            </Button>
            {tenant && (
              <Link className="nyk-link" to={`${tenantPath(tenant.id)}&tab=roles`}>
                Roles (optional: narrow scopes per WMS group)
              </Link>
            )}
          </StepActions>
        </>
      ),
    },
    {
      id: 'service-key',
      title: 'Service key',
      state: !tenant
        ? 'blocked'
        : activeKey
          ? 'done'
          : tenant.identityProvider === 'Workserver'
            ? 'todo'
            : 'optional',
      blocked: 'Pick or create the tenant first.',
      summary: activeKey ? 'active' : undefined,
      body: (
        <>
          <p>
            The WorkServer exchanges its user sessions for AUTH tokens with the tenant's service key (
            <code>Auth/ServiceKey</code>). Issuing a new one revokes the old one at once.
          </p>
          <Facts rows={[['Active key', activeKey ? `since ${activeKey.createdAt.slice(0, 10)}` : 'none']]} />
          {serviceKey && <SecretValue label="Service key" value={serviceKey} />}
          <StepActions error={action.error}>
            <Button
              variant={activeKey ? 'ghost' : 'primary'}
              loading={action.busy === 'key'}
              onClick={() =>
                void action.run('key', async () => {
                  const r = await client.tenants.serviceKeys.issue(tenantId);
                  rememberSecret(sk('service-key'), r.service_key);
                })
              }
            >
              {activeKey ? 'Issue a new key (revokes the old)' : 'Issue service key'}
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'aliases',
      title: 'Aliases',
      state: !tenant ? 'blocked' : wsAlias ? 'done' : 'todo',
      blocked: 'Pick or create the tenant first.',
      summary: s?.aliases.length ? s.aliases.map((a) => `${a.system}:${a.value}`).join(', ') : undefined,
      body: (
        <>
          <p>
            Aliases tell other systems which tenant an id of theirs means:{' '}
            <code>workserver:&lt;name&gt;</code> (the WorkServer's name, used by Statistics),{' '}
            <code>gke:&lt;cluster&gt;:&lt;namespace&gt;</code> (the installation's namespace). The print
            queue's alias is set by its wizard.
          </p>
          <div className="nyk-form">
            <TextField
              label="WorkServer name"
              value={ws}
              onChange={(e) => setWs(e.target.value.trim())}
              hint="WORKSERVER_NAME of the installation."
            />
            <TextField
              label="GKE cluster:namespace"
              value={gke}
              onChange={(e) => setGke(e.target.value.trim())}
              placeholder="gke-prod:nlc-prod-acme"
            />
          </div>
          <Facts rows={[['Aliases', s?.aliases.map((a) => `${a.system}:${a.value}`).join(', ') || 'none']]} />
          <StepActions error={action.error}>
            <Button
              disabled={!ws && !gke}
              loading={action.busy === 'aliases'}
              onClick={() =>
                void action.run('aliases', async () => {
                  if (ws) await addAlias(target, 'tenants', tenantId, 'workserver', ws);
                  if (gke) await addAlias(target, 'tenants', tenantId, 'gke', gke);
                  setGke('');
                })
              }
            >
              Add aliases
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'client',
      title: 'WorkServer service client',
      state: !tenant
        ? 'blocked'
        : !clientScopes.length
          ? 'optional'
          : wms
            ? 'done'
            : missing.length
              ? 'blocked'
              : 'todo',
      blocked: !tenant ? 'Pick or create the tenant first.' : 'Add the missing scopes first.',
      summary: wms ? `${wms.name} (${wms.scopes.join(', ')})` : undefined,
      body: (
        <>
          <p>
            The WorkServer calls the services with its own AUTH client (<code>client_credentials</code>),
            scopes{' '}
            {clientScopes.map((c) => (
              <code key={c}>{c} </code>
            ))}
            .
          </p>
          <Facts
            rows={[
              [
                'Client',
                wms ? `${wms.name} · ${wms.clientId}` : wmsAny ? `${wmsAny.name} lacks some scopes` : 'none',
              ],
              [
                'Secret',
                clientSecret ? 'issued now (in the files)' : 'not shown again; issue one for the files',
              ],
            ]}
          />
          {clientSecret && <SecretValue label="Client secret" value={clientSecret} />}
          <StepActions error={action.error}>
            {!wms && !wmsAny && (
              <Button
                disabled={!clientScopes.length}
                loading={action.busy === 'client'}
                onClick={() =>
                  void action.run('client', async () => {
                    const c = await client.tenants.clients.create(tenantId, {
                      name: ws ? `wms-${slug(ws)}` : 'wms',
                      description: 'WorkServer service calls (onboarding wizard)',
                      scopes: clientScopes,
                    });
                    const sec = await client.tenants.clients.secrets.issue(tenantId, c.id);
                    rememberSecret(sk('client-id'), c.clientId);
                    rememberSecret(sk('client-secret'), sec.client_secret);
                  })
                }
              >
                Create client and secret
              </Button>
            )}
            {!wms && wmsAny && (
              <Button
                loading={action.busy === 'client-scopes'}
                onClick={() =>
                  void action.run('client-scopes', () =>
                    client.tenants.clients.update(tenantId, wmsAny.id, {
                      name: wmsAny.name,
                      description: wmsAny.description,
                      status: wmsAny.status,
                      scopes: [...new Set([...wmsAny.scopes, ...clientScopes])],
                    })
                  )
                }
              >
                Add the scopes to {wmsAny.name}
              </Button>
            )}
            {(wms ?? wmsAny) && (
              <Button
                variant="ghost"
                loading={action.busy === 'secret'}
                onClick={() =>
                  void action.run('secret', async () => {
                    const c = (wms ?? wmsAny)!;
                    const sec = await client.tenants.clients.secrets.issue(tenantId, c.id);
                    rememberSecret(sk('client-id'), c.clientId);
                    rememberSecret(sk('client-secret'), sec.client_secret);
                  })
                }
              >
                Issue a secret
              </Button>
            )}
          </StepActions>
          {action.error?.includes('409') && (
            <Note tone="warn">
              Two secrets are active already: revoke one on the tenant's Clients tab first.
            </Note>
          )}
        </>
      ),
    },
    {
      id: 'environment',
      title: 'Hub environment',
      state: !tenant || !isHubAuth ? 'blocked' : linkedEnv ? 'done' : 'optional',
      blocked: !isHubAuth
        ? 'Only tenants of the hub’s own AUTH link to registry environments.'
        : 'Pick or create the tenant first.',
      summary: linkedEnv?.name,
      body: (
        <>
          <p>
            Link the hub's registry environment of this installation to the tenant: Statistics, logs and pods
            then know which customer it is. New installation? Register it with the{' '}
            <Link className="nyk-link" to="/onboarding/installation">
              Installation wizard
            </Link>
            .
          </p>
          {linkedEnv ? (
            <Facts rows={[['Environment', `${linkedEnv.name} (${linkedEnv.id})`]]} />
          ) : (
            <NeedsScope scope={SCOPES.admin}>
              <Select
                label="Environment"
                value={envId}
                onChange={setEnvId}
                searchable
                options={[
                  { value: '', label: '— pick —' },
                  ...(s?.envs ?? [])
                    .filter((e) => !e.discovered)
                    .map((e) => ({
                      value: e.id,
                      label: `${e.name} (${e.id})${e.authTenantId ? ' – linked elsewhere' : ''}`,
                    })),
                ]}
              />
              <StepActions error={action.error}>
                <Button
                  disabled={!envId}
                  loading={action.busy === 'env'}
                  onClick={() =>
                    void action.run('env', async () => {
                      const e = s!.envs.find((x) => x.id === envId)!;
                      const {
                        id: _i,
                        updatedAt: _u,
                        clusterName: _c,
                        dbServerName: _d,
                        discovered: _x,
                        ...input
                      } = e;
                      await api.admin.environments.put(envId, { ...input, authTenantId: tenantId });
                      if (!ns && e.namespaces[0]) setNs(e.namespaces[0]);
                    })
                  }
                >
                  Link environment
                </Button>
              </StepActions>
            </NeedsScope>
          )}
        </>
      ),
    },
    {
      id: 'workserver',
      title: 'WorkServer options',
      state: !tenant ? 'blocked' : wms || !clientScopes.length ? 'done' : 'blocked',
      blocked: 'Create the service client first.',
      summary: wms ? 'files ready' : undefined,
      body: env ? (
        <>
          <div className="nyk-form">
            <Select
              label="Client secret goes into"
              value={secretIn === 'options' ? 'options' : 'env'}
              onChange={setSecretIn}
              options={[
                { value: 'env', label: 'a Kubernetes secret (GKE)' },
                { value: 'options', label: 'the options (on-prem Windows service)' },
              ]}
            />
            <TextField
              label="Namespace"
              value={ns || linkedEnv?.namespaces[0] || ''}
              onChange={(e) => setNs(e.target.value.trim())}
              placeholder="nlc-prod-acme"
            />
          </div>
          {!env.collectorUrl && modules.includes('stats') && (
            <Note tone="warn">
              The collector URL is not configured for {env.label} (
              <code>Onboarding:Targets:{target}:CollectorUrl</code>
              ): fill in <code>RemoteDataCollector/Uri</code> by hand.
            </Note>
          )}
          <FileOutput
            zipName={`workserver-${ws || 'auth'}.zip`}
            files={workServerFiles(
              {
                authServiceUrl: env.authServiceUrl,
                userSessions:
                  tenant?.identityProvider === 'Workserver' && activeKey
                    ? { authority: env.authUrl, serviceKey: serviceKey ?? PLACEHOLDER }
                    : undefined,
                client: wms
                  ? {
                      clientId: secrets(sk('client-id')) ?? wms.clientId,
                      clientSecret: clientSecret ?? PLACEHOLDER,
                      secretIn: secretIn === 'options' ? 'options' : 'env',
                    }
                  : undefined,
                datacollector: modules.includes('stats')
                  ? { uri: env.collectorUrl ?? 'TODO-collector-URL', auth: 'Both' }
                  : undefined,
                boxCalculation: modules.includes('box')
                  ? { serviceUrl: env.boxCalculationUrl ?? 'TODO-box-calculation-URL', auth: 'Both' }
                  : undefined,
                printQueue:
                  modules.includes('pq') && env.printQueueUri
                    ? { serviceUrl: `${env.printQueueUri}/`, auth: 'Both' }
                    : undefined,
              },
              secretIn === 'options' ? undefined : ns || linkedEnv?.namespaces[0] || '<namespace>'
            )}
          />
          <p className="nyk-ui-hint">
            The datacollector and print queue tokens (the fallback while on <code>Both</code>) come from the
            Statistics and Print queue wizards.{' '}
            <Link
              className="nyk-link"
              to={guidePath('workserver-auth', {
                workserver: ws,
                namespace: ns || linkedEnv?.namespaces[0],
                authServiceUrl: env.authServiceUrl,
              })}
            >
              Guide
            </Link>
          </p>
        </>
      ) : null,
    },
  ];

  return (
    <Wizard
      steps={steps}
      loading={(dir.loading && !dir.data) || (state.loading && !!tenantId && !s)}
      error={dir.error ?? state.error}
      onRecheck={() => void reload()}
    />
  );
}
